import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import Modal from "react-modal";
import { useLanguage } from "../context/LanguageContext";

Modal.setAppElement("#root");

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const CONTACT_TO_EMAIL = process.env.REACT_APP_CONTACT_TO_EMAIL || "info@ir-accounting.ca";

const ContactForm = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      setErrorMessage(t("contactForm.errorNotConfigured"));
      setLoading(false);
      return;
    }

    const templateParams = {
      to_email: CONTACT_TO_EMAIL,
      from_name: formData.name,
      from_email: formData.email,
      from_phone: formData.phone,
      message: formData.message,
    };

    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
      setShowModal(true);
      setFormData({ name: "", phone: "", email: "", message: "" });
    } catch (error) {
      const details = error?.text || error?.message || t("contactForm.errorTryLater");
      console.error("EmailJS error:", error);
      setErrorMessage(`${t("contactForm.errorSendPrefix")} ${details}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="max-w-3xl w-full mx-auto bg-gradient-to-br from-white to-gray-50 p-8 rounded-xl shadow-lg border border-gray-300">
        <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">{t("contactForm.title")}</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-gray-700 font-medium">
              {t("contactForm.nameLabel")}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t("contactForm.namePlaceholder")}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-600 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="block text-gray-700 font-medium">
                {t("contactForm.phoneLabel")}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={t("contactForm.phonePlaceholder")}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-600 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                required
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-gray-700 font-medium">
                {t("contactForm.emailLabel")}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t("contactForm.emailPlaceholder")}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-600 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="message" className="block text-gray-700 font-medium">
              {t("contactForm.messageLabel")}
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder={t("contactForm.messagePlaceholder")}
              className="w-full h-32 px-4 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-600 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
              rows="5"
              required
            ></textarea>
          </div>

          {errorMessage && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <FaExclamationCircle className="mt-0.5 shrink-0" />
              <p>{errorMessage}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 text-lg rounded-lg font-semibold text-white transition-all duration-300 ${
              loading ? "bg-gray-400 cursor-not-allowed" : "bg-yellow-600 hover:bg-yellow-500 hover:scale-105"
            }`}
          >
            {loading ? t("contactForm.sendingButton") : t("contactForm.submitButton")}
          </button>
        </form>
      </div>

      <Modal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        className="bg-white p-8 rounded-xl shadow-xl max-w-lg mx-auto z-50"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      >
        <FaCheckCircle className="text-4xl text-green-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900">{t("contactForm.successTitle")}</h2>
        <p className="text-gray-700 mt-2">{t("contactForm.successBody")}</p>
        <button
          onClick={() => setShowModal(false)}
          className="mt-6 bg-yellow-600 hover:bg-yellow-500 text-white py-3 px-8 rounded-lg transition-all duration-300"
        >
          {t("contactForm.closeButton")}
        </button>
      </Modal>
    </>
  );
};

export default ContactForm;
